<template>
  <div>
    <div class="welcome-bar">
      <h3 class="welcome-bar-text text-brown">Donation Requests Manager</h3>
      <h4 class="welcome-bar-text-sub">Please select a request to view more information regarding its status.</h4>
    </div>

    <div class="filter-bar">
      <span v-if="nothingInTable">- Please select at least one filter to view applications -</span>
      <span v-else>Filter Bar</span>
      <span class="filter-bar-button" @click="filtersExpanded = !filtersExpanded">{{ filterButton }}</span>
    </div>

    <div class="container" style="min-width: 1100px;">
      <div v-if="filtersExpanded" class="filter-container">
        <div class="button-box-container">
          <button-box box-header="type">
            <BDropdown
              text="Request Type"
              class="top-filter-dropdown"
              split
              :class="{'isActive' : eventTypeFilter}"
              @click.prevent="clearEventTypeFilter">
                <BDropdownItemButton
                  v-for="(item, index) in availableEventTypeFilters"
                  :key="index"
                  class="dropdown-item"
                  :class="{'isActive' : item.filter == eventTypeFilter}"
                  @click.prevent="setEventTypeFilter(item.filter)">{{item.filter}}</BDropdownItemButton>
            </BDropdown>
          </button-box>
          <button-box box-header="status">
            <button
              class="bottom-filter-button"
              :class="{'isActive' : statusFilters.indexOf('awaiting_approval') >= 0}"
              @click.prevent="addStatusFilter('awaiting_approval')">Awaiting Approval</button>
            <button
              class="bottom-filter-button"
              :class="{'isActive' : statusFilters.indexOf('denied') >= 0}"
              @click.prevent="addStatusFilter('denied')">Rejected</button>
            <button
              class="bottom-filter-button"
              :class="{'isActive' : statusFilters.indexOf('approved') >= 0}"
              @click.prevent="addStatusFilter('approved')">Approved</button>
            <button
                    class="bottom-filter-button"
                    :class="{'isActive' : statusFilters.indexOf('mailed') >= 0}"
                    @click.prevent="addStatusFilter('mailed')">Mailed</button>
            <button
                    class="bottom-filter-button"
                    :class="{'isActive' : statusFilters.indexOf('archived') >= 0}"
                    @click.prevent="addStatusFilter('archived')">Archived</button>
          </button-box>
        </div>
        <div class="button-box-container">
          <button-box box-header="status change">
            <BFormSelect
              class="actions__action-box-select-status"
              v-model="newStatus"
              :options="statusOptions"
              @update:model-value="bulkUpdateStatus"/>
            <button
              class="update-button commit-button"
              :class="{'noncommittal' : noncommittal}"
              :disabled="noncommittal"
              @click.prevent="commitChanges">Commit Status Change</button>
          </button-box>
          <button-box box-header="filter by keyword">
            <input type="text" class="fuzzy-search" v-model="fuzzyFilter" placeholder="Type to Search" @input="doTheFuzzy"/>
            <button class="search-clear" :disabled="fuzzyFilter == ''" @click="clearFilters">Clear</button>
          </button-box>
        </div>

        <div class="button-box-container mt-5">
          <button-box >
            <a class="btn search-clear" target="_blank" :href="'/donations/export?' + getHttpSearchParams()">Export To Spreadsheet</a>
          </button-box>
          <div class="button-box d-flex">
            <div class="inner-box">
              <h4 class="button-box__header">Event Date</h4>
              <date-picker 
                id="eventDateRange"
                ref="eventDatePicker"
                v-model:value="event_date"
                placeholder='Date Range'
                range=true
                class="program-details__date-field"
                input-class="datepicker-range"
                format="MM/DD/YYYY"
                name="startDate"
                @change="onEventDateChanged"
                @clear="clearEventDates"
                @focus="onFocusEventDate">
              </date-picker>
            </div>

            <div class="inner-box">
              <h4 class="button-box__header">Request Date</h4>
              <date-picker 
                id="requestDateRange"
                ref="requestDatePicker"
                v-model:value="request_date"
                placeholder='Date Range'
                range=true
                class="program-details__date-field"
                input-class="datepicker-range"
                format="MM/DD/YYYY"
                name="startDate"                   
                @change="onDateChangedRequest"
                @clear="clearRequestDates"
                @focus="onFocusRequestDate">
              </date-picker>
            </div>
          </div>
        </div>
      </div>

      <error-box
        :general-errors="generalErrors"
        :has-errors="hasErrors"/>

      <p v-if="pagination.total" class="mt-3 approved-rewards text-brown">
        Rows {{ pagination.from}} - {{ pagination.to }} of {{ pagination.total }}
      </p>
      
      <b-pagination
        v-model="currentPage" 
        :total-rows="pagination.total"
        :per-page="perPage"  
        v-if="pagination.last_page > 1" 
        aria-controls="my-table"
        @page-click="onPageChange" 
      ></b-pagination>

      <div class="table-responsive">
        <BTable
          striped
          hover
          :items="donationRequests"
          :fields="fields"
          :filter="filter"
          :per-page="50"
          id="my-table"
          class="admin-app-table actions__table">
          <template #head(id)="item" >
            <button @click.stop="selectAllRows" class="selector-red-fill" :class="{'isActive' : selectAll}"/>
          </template>
          <template #head(view)="item" >
            <button class="view-details-button" @click="toggleAll()">
              {{ showAll ? 'Shrink' : 'Expand' }} All
            </button>
          </template>
          <template #head(requestDate)="item" >
            <span @click="setSort('donation_requests.created_at')" class="table-title" :class="{'table_header-sortable': true, 'isActive' : sortBy == 'donation_requests.created_at'}">Request Date</span>
          </template>
          <template #head(eventDate)="item" >
            <span @click="setSort('donation_requests.event_date')" class="table-title" :class="{'table_header-sortable': true, 'isActive' : sortBy == 'donation_requests.event_date'}">Event Date</span>
          </template>
          <template #head(eventType)="item" >
            <span @click="setSort('event_type')" class="table-title" :class="{'table_header-sortable': true, 'isActive' : sortBy == 'event_type'}">Type</span>
          </template>
          <template #head(organization)="item" >
            <span @click="setSort('organization_name')" class="table-title" :class="{'table_header-sortable': true, 'isActive' : sortBy == 'organization_name'}">Organization</span>
          </template>
          <template #head(applicantName)="item" >
            <span @click="setSort('applicant_first_name')" class="table-title" :class="{'table_header-sortable': true, 'isActive' : sortBy == 'applicant_first_name'}">Contact Name</span>
          </template>
          <template #head(applicantEmail)="item" >
            <span @click="setSort('applicant_email')" class="table-title" :class="{'table_header-sortable': true, 'isActive' : sortBy == 'applicant_email'}">Contact Email</span>
          </template>
          <template #head(status)="item" >
            <span @click="setSort('application_status')" class="table-title" :class="{'table_header-sortable': true, 'isActive' : sortBy == 'application_status'}">Status</span>
          </template>
          <template #head(changedBy)="item" >
            <span  class="table-title" >Changed By</span>
          </template>
          <template #head(flags)="item" >
            <span  class="table-title" >Flags</span>
          </template>
          <template #cell(id)="item" >
            <button
              class="selector-red-fill"
              :class="{'isActive' : selectedRows.indexOf(item.item.id) >= 0}"
              @click="selectThisRow(item.item)"/>
          </template>
          <template #cell(view)="row">
            <button class="view-details-button" @click="row.toggleDetails">
              {{ row.detailsShowing ? 'Less' : 'More' }}
            </button>
          </template>

          <template #cell(status)="item" >
            <span :class="item.item.status">
              <svg-icon v-if="statusIcon(item.item.status) == 'faEnvelope'" :fa-icon="faEnvelope"/>
              <svg-icon v-if="statusIcon(item.item.status) == 'faCircle'" :fa-icon="faCircle"/>
              <svg-icon v-if="statusIcon(item.item.status) == 'faCheck'" :fa-icon="faCheck"/>
              <svg-icon v-if="statusIcon(item.item.status) == 'faXmark'" :fa-icon="faXmark"/>
              <svg-icon v-if="statusIcon(item.item.status) == 'faArchive'" :fa-icon="faArchive"/>
            </span>
          </template>

          <template #cell(requestDate)="item" >
            {{ $filters.formatDate(item.item.requestDate) }}
          </template>

          <template #cell(eventDate)="item" >
            {{ $filters.formatDate(item.item.eventDate) }}
          </template>

          <template #row-details="row" >
            <DetailsBox
              :row-data="row.item"
              :award-types="awardTypes"
              :organization-types="organizationTypes"
              :states="states"
              @addWaiting="addWaiting(row.item.id, true)"
              @addApproved="addApproved(row.item.id, true)"
              @addDenied="addDenied(row.item.id, true)"
              @addMailed="addMailed(row.item.id, true)"
              @addArchived="addArchived(row.item.id, true)"
              @rowUpdated="updateRow"/>
          </template>

          <template #cell(changedBy)="item" >
            <span>{{ $filters.activityUser(item.item.changedBy) }} {{ item.item.changedBy != "" ? " - " + $filters.activityTime(item.item.changedBy) : ""}} </span>
          </template>

          <template #cell(flags)="item" >
            <span v-if="item.item.emailHasAppliedThisYear || item.item.addressHasAppliedThisYear">
              <img
                :id="'flag-'+item.item.id"
                src="/images/flag-red.png"
                class="">
              <BTooltip :target="'flag-'+item.item.id" variant="danger">{{ flagReasons(item.item) }}</BTooltip>
            </span>
          </template>

        </BTable>
      </div>

      <p v-if="pagination.total" class="mt-3 approved-rewards text-brown">
        Rows {{ pagination.from}} - {{ pagination.to }} of {{ pagination.total }}
      </p>

      <b-pagination
        v-model="currentPage" 
        :total-rows="pagination.total"
        :per-page="perPage"  
        v-if="pagination.last_page > 1" 
        aria-controls="my-table"
        @page-click="onPageChange"
      ></b-pagination>      

    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';

  // Import Components
  import ButtonBox from '../boxes/buttonBox.vue'
  import ErrorBox from '../boxes/errorBox.vue'
  import DetailsBox from '../boxes/detailsBox.vue';

  // Import Icons
  import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
  import { faCircle } from '@fortawesome/free-solid-svg-icons';
  import { faArchive } from '@fortawesome/free-solid-svg-icons';
  import { faCheck } from '@fortawesome/free-solid-svg-icons';
  import { faXmark } from '@fortawesome/free-solid-svg-icons';

  import DatePicker from 'vue-datepicker-next';
  import 'vue-datepicker-next/index.css';

  const event_date = ref('');
  const request_date = ref('');
  const filtersExpanded = ref(true);
  //const currentPage = ref(1);
  const perPage = ref(50);

  const availableEventTypeFilters= ref([
    { filter: 'Silent Auction' },
    { filter: 'Raffle' },
    { filter: 'Other' }
  ]);

  const fields = ref([
    { key: 'id' },
    { key: 'view' },
    {
      key: 'requestDate',
      label: '',
      sortable: false
    },
    {
      key: 'eventDate',
      label: '',
      sortable: false
    },
    {
      key: 'eventType',
      label: '',
      sortable: false
    },
    {
      key: 'organization',
      label: '',
      sortable: false
    },
    {
      key: 'applicantName',
      label: '',
      sortable: false
    },
    {
      key: 'applicantEmail',
      label: '',
      sortable: false,
    },
    {
      key: 'status',
      label: '',
      sortable: false
    },
    {
      key: 'changedBy',
      label: '',
      sortable: false,
    },
    {
      key: 'flags',
      label: '',
      sortable: false
    }
  ]);

  const statusOptions = ref([
    { value: 'do_nothing', text: 'Change Selected Rows' },
    { value: 'awaiting_approval', text: 'Pending' },
    { value: 'approved', text: 'Approved' },
    { value: 'denied', text: 'Rejected' },
    { value: 'mailed', text: 'Mailed' },
    { value: 'archived', text: 'Archived' }
  ]);  

  const filterButton = computed (() => {
      return filtersExpanded.value ? 'Collapse Filters' : 'Expand Filters'
  });    
</script>

<script>
  import _ from 'lodash'
  import * as moment from 'moment';

  export default {
    props: ['awardTypes', 'organizationTypes', 'states'],
    data() {
      return {
        totalRows: 0,
        currentPage: 1,
        showAll: false,
        sortBy: 'organization_name',
        hasErrors: false,
        generalErrors: [],
        filter: '',
        fuzzyFilter: '',
        setApproved: [],
        setDenied: [],
        setWaiting: [],
        setMailed: [],
        setArchived: [],
        newStatus: 'do_nothing',
        typesToUpdate: [],
        filteredTable: [],
        eventTypeFilter: '',
        sortBy: 'organization_name',
        orderBy: 'asc',
        rawData: [],
        pagination: [],
        donationRequests: [],
        allOrgFiltersActive: false,
        selectedRows: [],
        selectAll: false,
        statusFilters: [
          'awaiting_approval'
        ],
        //filtersExpanded: true,
        nothingInTable: true,
        noncommittal: true,

        // Datepicker
        today: new Date(),
        dateEventRange: '',
        startEventDate: '',
        endEventDate: '',
        dateRangeRequest: '',
        startRequestDate: '',
        endRequestDate: '',
        lang: {
          days: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          // pickers: [''],
          placeholder: {
            date: 'Select Date',
            dateRange: 'Select Date Range'
          }
        }
      }
    },
    computed: {
      statusFilterString() {
        return this.statusFilters != [] ? this.statusFilters.join() : ''
      },
    },

    mounted() {
      this.getData()
    },

    methods: {
      resetPagination() {
        this.currentPage = 1
        this.pagination.current_page = 1
      },
      onPageChange(e) {
        this.pagination.current_page = e.target.textContent
        this.getData()
      },
      getData() {
        this.hasErrors = false
        this.generalErrors = []
        axios.get('/donations/ajax/applications-by-params?page=' + this.pagination.current_page, {
          params: {
            status: this.statusFilterString,
            type: this.eventTypeFilter,
            sortBy: this.sortBy,
            order: this.orderBy,
            search: this.fuzzyFilter,
            startDate: this.startEventDate,
            endDate: this.endEventDate,
            startDateRequest: this.startRequestDate,
            endDateRequest: this.endRequestDate
          }
        })
        .then(response => {
          this.rawData = response.data
          this.pagination = this.formatPagination(response.data)
          this.donationRequests = this.formatData(response.data.data)
          this.showAll = false
        })
        .catch(e => {
          console.error("There was an error!", e)
          this.handleError(e)
        })
        this.nothingInTable = false
        this.resetSomeThings()
      },

      formatPagination(response) {
        return {
          total: response.total,
          per_page: response.per_page,
          current_page: response.current_page,
          last_page: response.last_page,
          from: response.from,
          to: response.to
        }
      },

      formatData(data) {
        return data.map(item => {
          return {
            id: item.id,
            requestDate: item.created_at,
            eventType: item.event_type,
            eventOtherType: item.event_other_type,
            eventCustomType: item.event_custom,
            eventAttendees: item.event_attendees_count,
            eventDate: item.event_date,
            eventDescription: item.event_description,
            organization: item.organization_name,
            organizationCountry: item.organization_country,
            organizationAddress: item.organization_address,
            organizationAddress2: item.organization_address2,
            organizationCity: item.organization_city,
            organizationState: item.organization_state,
            organizationZip: item.organization_zipcode,
            organizationEin: item.organization_ein,
            organizationType: item.organization_type,
            applicantName: item.applicant_first_name + ' ' + item.applicant_last_name,
            applicantEmail: item.applicant_email,
            applicantPhone: item.applicant_phone,
            status: item.application_status,
            deniedMessage: item.denied_message,
            changedBy: item.activities,
            emailHasAppliedThisYear: item.emailHasAppliedThisYear,
            addressHasAppliedThisYear: item.addressHasAppliedThisYear,
            award: item.awards[0],
            _showDetails: false // So that we can expand / shrink all
          }
        })
      },

      resetSomeThings() {
          this.selectedRows = []
          this.selectAll = false
          this.newStatus = 'do_nothing'
          this.noncommittal = true
          this.setApproved = []
          this.setDenied = []
          this.setWaiting = []
          this.setMailed = []
          this.setArchived = []
          this.typesToUpdate = []
      },

      setSort(sorter) {
        if (this.sortBy == sorter) {
          this.orderBy = (this.orderBy == 'desc') ? 'asc' : 'desc'
        } else {
          this.sortBy = sorter
          this.orderBy = 'asc'
        }
        this.getData()
      },

      toggleAll() {
        this.showAll = !this.showAll
        this.donationRequests.forEach( (row) => this.toggleRowAll(row))
      },

      showRow(row, test) {
        row._showDetails = this.showAll
      },

      toggleRowAll(row) {
        row._showDetails = this.showAll
      },

      clearFilters() {
        this.fuzzyFilter = ''
        this.resetPagination()
        this.donationRequests = this.getData()
      },

      addStatusFilter(type) {
        this.statusFilters.indexOf(type) >= 0 ? this.statusFilters.splice(this.statusFilters.indexOf(type), 1) : this.statusFilters.push(type)
        setTimeout(() => {
          this.resetPagination()
          this.donationRequests = this.getData()
        }, 1000)
      },

      selectAllRows() {
        if (this.selectAll === false) {
          if (this.filter) {
            for (var i = 0; i < this.filteredTable.length; i++) {
              this.selectedRows.push(this.filteredTable[i].id)
            }
          } else {
            for (var i = 0; i < this.donationRequests.length; i++) {
              this.selectedRows.push(this.donationRequests[i].id)
            }
          }
            this.selectAll = true
        } else {
          this.resetSomeThings()
        }
      },

      selectThisRow(item) {
        if (this.selectedRows.indexOf(item.id) >= 0) {
          this.selectedRows.splice(this.selectedRows.indexOf(item.id), 1)
        } else {
          this.selectedRows.push(item.id)
        }
      },

      setEventTypeFilter(type) {
        if (this.eventTypeFilter == type) {
          this.clearEventTypeFilter()
        } else {
          this.eventTypeFilter = type
          setTimeout(() => {
            this.resetPagination()
            this.donationRequests = this.getData()
          }, 1000)
        }
      },

      clearEventTypeFilter() {
        this.eventTypeFilter = ''
        setTimeout(() => {
          this.resetPagination()
          this.donationRequests = this.getData()
        }, 1000)
      },

      refreshDataSet() {
        if (this.groupingByOrganization) {
          this.resetPagination()
          this.donationRequests = this.getData
        } else if (this.groupingByRestaurant) {
          this.resetPagination()
          this.donationRequests = this.getDataByRestaurant
        } else if (this.groupingByApplicants) {
          this.resetPagination()
          this.donationRequests = this.getDataByApplicants
        }
      },

      doTheFuzzy: _.debounce(function(){
        this.resetPagination()
        this.donationRequests = this.getData()
      }, 2000),

      bulkUpdateStatus() {
        if (this.newStatus === 'do_nothing') {
          this.selectedRows = this.selectedRows
        } else {
          for (var i = 0; i < this.selectedRows.length; i++) {
            if (this.newStatus === 'approved') {
                this.addApproved(this.selectedRows[i], false)
            } else if (this.newStatus === 'denied') {
                this.addDenied(this.selectedRows[i], false)
            } else if (this.newStatus === 'awaiting_approval') {
                this.addWaiting(this.selectedRows[i], false)
            } else if (this.newStatus === 'mailed') {
              this.addMailed(this.selectedRows[i], false)
            } else if (this.newStatus === 'archived') {
              this.addArchived(this.selectedRows[i], false)
            }
          }
        }
        this.noncommittal = false
      },      
      addApproved(id, deselectRow) {
        if (_.includes(this.setDenied, id)) {
          _.pull(this.setDenied, id)
        } else if (_.includes(this.setWaiting, id)) {
          _.pull(this.setWaiting, id)
        } else if (_.includes(this.setMailed, id)) {
          _.pull(this.setMailed, id)
        } else if (_.includes(this.setArchived, id)) {
          _.pull(this.setArchived, id)
        }

        if (!_.includes(this.setApproved, id)) {
          this.setApproved.push(id)
        }
        if (deselectRow) {
          this.selectedRows.splice(this.selectedRows.indexOf(id), 1)
        }
        this.noncommittal = false
      },

      addDenied(id, deselectRow) {
        if (this.setApproved.indexOf(id) >= 0) {
          this.setApproved.splice(this.setApproved.indexOf(id), 1)
        } else if (this.setWaiting.indexOf(id) >= 0) {
          this.setWaiting.splice(this.setWaiting.indexOf(id), 1)
        } else if (_.includes(this.setMailed, id)) {
          _.pull(this.setMailed, id)
        } else if (_.includes(this.setArchived, id)) {
          _.pull(this.setArchived, id)
        }

        if (this.setDenied.indexOf(id) >= 0) {
          this.setDenied[this.setDenied.indexOf(id)] = id
        } else {
          this.setDenied.push(id)
        }
        if (deselectRow) {
          this.selectedRows.splice(this.selectedRows.indexOf(id), 1)
        }
        this.noncommittal = false
      },

      addWaiting(id, deselectRow) {
        if (this.setDenied.indexOf(id) >= 0) {
          this.setDenied.splice(this.setDenied.indexOf(id), 1)
        } else if (this.setApproved.indexOf(id) >= 0) {
          this.setApproved.splice(this.setApproved.indexOf(id), 1)
        } else if (_.includes(this.setMailed, id)) {
          _.pull(this.setMailed, id)
        } else if (_.includes(this.setArchived, id)) {
          _.pull(this.setArchived, id)
        }

        if (this.setWaiting.indexOf(id) >= 0) {
          this.setWaiting[this.setWaiting.indexOf(id)] = id
        } else {
          this.setWaiting.push(id)
        }
        if (deselectRow) {
          this.selectedRows.splice(this.selectedRows.indexOf(id), 1)
        }
        this.noncommittal = false
      },

      addMailed(id, deselectRow) {
        if (_.includes(this.setDenied, id)) {
          _.pull(this.setDenied, id)
        } else if (_.includes(this.setWaiting, id)) {
          _.pull(this.setWaiting, id)
        } else if (_.includes(this.setApproved, id)) {
          _.pull(this.setApproved, id)
        } else if (_.includes(this.setArchived, id)) {
          _.pull(this.setArchived, id)
        }

        if (!_.includes(this.setMailed, id)) {
          this.setMailed.push(id)
        }
        if (deselectRow) {
          this.selectedRows.splice(this.selectedRows.indexOf(id), 1)
        }
        this.noncommittal = false
      },

      addArchived(id, deselectRow) {
        if (_.includes(this.setDenied, id)) {
          _.pull(this.setDenied, id)
        } else if (_.includes(this.setWaiting, id)) {
          _.pull(this.setWaiting, id)
        } else if (_.includes(this.setApproved, id)) {
          _.pull(this.setApproved, id)
        } else if (_.includes(this.setMailed, id)) {
          _.pull(this.setMailed, id)
        }

        if (!_.includes(this.setArchived, id)) {
          this.setArchived.push(id)
        }
        if (deselectRow) {
          this.selectedRows.splice(this.selectedRows.indexOf(id), 1)
        }
        this.noncommittal = false
      },

      updateRow(formObject) {
        // Remove form object if currently in array
        for (var i = 0; i < this.typesToUpdate.length; i++) {
          if (this.typesToUpdate[i].id == formObject.id) {
            this.typesToUpdate.splice(i, 1)
          }
        }
        // Push formObject anew to array
        this.typesToUpdate.push(formObject)
        this.noncommittal = false
      },

      commitChanges() {
        axios.post('/donations/ajax/application-updates', {
          approve: this.setApproved,
          deny: this.setDenied,
          pending: this.setWaiting,
          mailed: this.setMailed,
          archived: this.setArchived,
          types: this.typesToUpdate,
        })
        .then(response => {
          this.getData()
          this.noncommittal = true
        })
        .catch(e => {
          this.handleError(e)
        })
      },

      handleError(e) {
        this.generalErrors = []
        this.hasErrors = false
        // If not error message for some reason...
        if (!e.response) {
          this.hasErrors = false
        } else if (e.response.data.errors) {
          this.hasErrors = true
          for (var key in e.response.data.errors) {
            this.generalErrors.push(e.response.data.errors[key][0])
          }
        } else {
          this.hasErrors = true
          this.generalErrors.push(
            "We're sorry, but it looks like there was a network error. Please try again later. "
          )
        }
      },

      onEventDateChanged(value) {
        if (!value[0]) {
          this.startEventDate = ''
          this.endEventDate = ''
        } else {
          this.startEventDate = moment(value[0]).format('YYYY-MM-DD')
          this.endEventDate = moment(value[1]).format('YYYY-MM-DD')
        }
        this.resetPagination()
        this.donationRequests = this.getData()
      },

      onDateChangedRequest(value) {
        if (!value[0]) {
          this.startRequestDate = ''
          this.endRequestDate = ''
        } else {
          this.startRequestDate = moment(value[0]).format('YYYY-MM-DD')
          this.endRequestDate = moment(value[1]).format('YYYY-MM-DD')
        }
        this.resetPagination()
        this.donationRequests = this.getData()
      },

      getHttpSearchParams() {
        let query = ''
        this.statusFilterString != '' ? query+= 'status=' + this.statusFilterString : query+= 'status='
        this.eventTypeFilter != '' ? query+= '&type=' + this.eventTypeFilter : query+= '&type='
        this.sortBy != '' ? query+= '&sortBy=' + this.sortBy : query+= '&sortBy='
        this.orderBy != '' ? query+= '&order=' + this.orderBy : query+= '&order='
        this.fuzzyFilter != '' ? query+= '&search=' + this.fuzzyFilter : query+= '&search='
        this.startEventDate != '' ? query+= '&startDate=' + this.startEventDate : query+= '&startDate='
        this.endEventDate != '' ? query+= '&endDate=' + this.endEventDate : query+= '&endDate=',
        this.startRequestDate != '' ? query+= '&startDateRequest=' + this.startRequestDate : query+= '&startDateRequest='
        this.endRequestDate != '' ? query+= '&endDateRequest=' + this.endRequestDate : query+= '&endDateRequest='
        return query
      },

      clearEventDates() {
        this.dateRange = ''
        this.startEventDate = ''
        this.endEventDate = ''
        this.resetPagination()
        this.donationRequests = this.getData()
      },

      clearRequestDates() {
        this.dateRangeRequest = ''
        this.startRequestDate = ''
        this.endRequestDate = ''
        this.resetPagination()
        this.donationRequests = this.getData()
      },

      onFocusEventDate() {
        let requestDatePicker = this.$refs.requestDatePicker
        requestDatePicker.closePopup()
      },

      onFocusRequestDate() {
        let eventDatePicker = this.$refs.eventDatePicker
        eventDatePicker.closePopup()
      },

      //finds a specific item based on the provided ID and toggles details on that item
      toggleDetails(row) {
        if(row._showDetails){
          row._showDetails = false
        }else{
          row._showDetails = true
        }
      },
      
      flagReasons: function(donation) {
        if (donation.emailHasAppliedThisYear && donation.addressHasAppliedThisYear) {
          return 'This email and this address have been used in at least one other request this last year.'
        }
        else if (donation.emailHasAppliedThisYear) {
          return 'This email has been used in at least one other request this last year.'
        }
        else if (donation.addressHasAppliedThisYear) {
          return 'This address has been used in at least one other request this last year.'
        }
        else return ''
      },

      statusIcon: function(status) {
        if (status === 'awaiting_approval') {
          return 'faCircle'
        } else if (status === 'approved') {
          return 'faCheck'
        } else if (status === 'denied') {
          return 'faXmark'
        } else if (status === 'mailed') {
          return 'faEnvelope'
        } else if (status === 'archived') {
          return 'faArchive'
        }
        return ''
      },       
    },

    filters: {

    }
  }
</script>
